import { NotificationService } from './../../../core/services/notification.service';
import { LanguageService } from './../../../core/services/language.service';
import { CommonModule } from '@angular/common';
import { ProfileResponse, updateMyPasswordInput, updateMyProfileInput } from '../../../core/models/profile.model';
import { AuthService } from '../../../core/services/auth.service';
import { Component, ElementRef, EventEmitter, HostListener, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslationPipe } from '../../pipe/translate.pipe';
import { ProfileService } from '../../../core/services/profile.service';
import { take } from 'rxjs/operators';
import { ResetPasswordResponse } from '../../../core/models/auth.model';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslationPipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  userProfile!: ProfileResponse;
  userId!: string;
  firstName!: string;
  showDropdown: boolean = false;
  isModalOpen = false;
  modalMessage = '';
  isProfileModalOpen = false;
  activeTab: 'profile' | 'changepassword' = 'profile';

  // Password visibility toggles
  showOldPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  profileForm!: FormGroup;
  changePasswordForm!: FormGroup;
  firstChar!: string;
  languages = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' }
  ];

  selectedLanguage = 'en';

  @Output() toggleSidebar = new EventEmitter<void>();
  @ViewChild('dropdownRef') dropdownRef!: ElementRef;

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private profileService: ProfileService,
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.authService.getMyProfile().pipe(take(1)).subscribe({
      next: (profile) => {
        this.userProfile = profile;
        this.firstName = this.userProfile.firstname;
        this.userId = this.userProfile.id;
        this.profileService.setProfile(profile)
        this.firstChar = this.firstName?.charAt(0)?.toUpperCase()
        this.initializeForms();
      },
      error: (error) => console.error('Error fetching profile:', error)
    });
    this.languageService.getLanguage();
  }

  initializeForms() {
    this.profileForm = this.fb.group({
      first_name: [this.userProfile.firstname, Validators.required],
      last_name: [this.userProfile.lastname, Validators.required],
      mobile: [this.userProfile.mobile, [Validators.required, Validators.pattern(/^\d{10}$/)]],
      email: [{ value: this.userProfile.email, disabled: true }, Validators.required],
      country_code: [this.userProfile.country_code || '+91', Validators.required]
    });

    this.changePasswordForm = this.fb.group({
      current_password: ['', Validators.required],
      new_password: ['', [Validators.required, Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/)]],
      confirm_password: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  // Custom validator to ensure new password and confirm password match
  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('new_password');
    const confirmPassword = form.get('confirm_password');
    return newPassword?.value === confirmPassword?.value
      ? null
      : { passwordMismatch: true };
  }

  // Toggle password visibility
  togglePasswordVisibility(field: 'old' | 'new' | 'confirm') {
    switch(field) {
      case 'old':
        this.showOldPassword = !this.showOldPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  saveProfile() {
    if (this.profileForm.valid) {
      const profileData: updateMyProfileInput = {
        first_name: this.profileForm.get('first_name')?.value,
        last_name: this.profileForm.get('last_name')?.value,
        mobile: this.profileForm.get('mobile')?.value,
        country_code: this.profileForm.get('country_code')?.value
      };

      this.profileService.updateMyProfile(profileData).subscribe({
        next: () => {
          this.notificationService.show('Profile updated successfully!', 'success');
          this.profileService.setProfile(profileData)
          this.closeProfileModal();
          this.getProfile()
        },
        error: (error) => {
          this.notificationService.show('Error Updating the Profile!', 'error');
          console.error(error);
        }
      });
    }
  }

  changePassword() {
    if (this.changePasswordForm.valid) {
      const passwordData: updateMyPasswordInput = {
        current_password: this.changePasswordForm.get('current_password')?.value,
        new_password: this.changePasswordForm.get('new_password')?.value
      };

      this.profileService.updateMyPassword(passwordData).pipe(take(1)).subscribe({
        next: (res: ResetPasswordResponse) => {
          if (res.success) {
            this.notificationService.show('Password changed successfully!', 'success');
            this.changePasswordForm.reset();
            this.closeProfileModal();
          }
        },
        error: (error) => {
          this.notificationService.show(error, 'error');
          console.error(error);
        }
      });
    }
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  openProfileModal() {
    this.isProfileModalOpen = true;
  }

  closeProfileModal() {
    this.isProfileModalOpen = false;
    this.changePasswordForm.reset();
  }

  // openConfirmModal() {
  //   this.profileService.getControllerOTP().subscribe({
  //     next: (data: ControllerOTP[]) => {
  //       if (data.length > 0) {
  //         const otpData = data[0];
  //         const formattedOtp = otpData.otp.split('').join(' ');

  //         this.modalMessage = `
  //           Hello,<br><br>
  //           <strong>${otpData.user}</strong> from <strong>${otpData.organization}</strong> has requested to connect your Cue Home Device.<br><br>
  //           Kindly share the below OTP with them if you agree to connect:<br><br>
  //           <strong style="font-size: 20px;">OTP: ${formattedOtp}</strong><br><br>
  //           Do not share if you have any issues.<br>
  //           Feel free to contact support: <a href="mailto:support@cuehome.com">support@cuehome.com</a>
  //         `;
  //         this.isModalOpen = true;
  //       } else {
  //         this.modalMessage = `
  //           No OTP requests found at the moment.<br><br>
  //           If you were expecting one, please try again later or contact support.
  //         `;
  //         this.isModalOpen = true;
  //       }
  //     },
  //     error: (err) => {
  //       console.error('Failed to fetch controller OTP:', err);
  //       this.notificationService.show('Something went wrong', 'error')
  //       // this.modalMessage = `
  //       //   Something went wrong while fetching the OTP.<br><br>
  //       //   Please try again later or contact support.
  //       // `;
  //       // this.isModalOpen = true;
  //     }
  //   });
  // }

  closeModal() {
    this.isModalOpen = false;
    this.getProfile()
  }

  getProfile(){
    this.profileService.getMyProfile().pipe(take(1)).subscribe({
      next: (profile) => {
        this.userProfile = profile;
        this.firstName = this.userProfile.firstname;
      },
      error: (error) => console.error('Error fetching profile:', error)
    });
  }
  confirmAction() {
    this.closeModal();
  }

  changeLanguage() {
    this.languageService.setLanguage(this.selectedLanguage);
  }

  logout() {
    this.authService.logout();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (
      this.showDropdown &&
      this.dropdownRef &&
      !this.dropdownRef.nativeElement.contains(event.target)
    ) {
      this.showDropdown = false;
    }
  }
}
